import React, { useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { GLOSSARY, GlossaryKey } from '../copy/glossary';

export interface InfoPopoverProps {
  entry: GlossaryKey;
  // Optional override for the trigger icon size.
  size?: number;
}

/**
 * Small "ⓘ" button that opens a Popover with a one-paragraph glossary entry.
 * Used next to jargon so users can self-serve definitions without tooltips.
 */
export const InfoPopover: React.FC<InfoPopoverProps> = ({ entry, size = 16 }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { term, body } = GLOSSARY[entry];

  return (
    <>
      <IconButton
        aria-label={`About ${term}`}
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ p: 0.25, opacity: 0.6, '&:hover': { opacity: 1 } }}
      >
        <InfoOutlinedIcon sx={{ fontSize: size }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {term}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {body}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default InfoPopover;
